import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { Spinner } from './ui/spinner';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useLocale, useT } from '../i18n/locale-context';
import { formatDeadline } from '../lib/format';
import { nextChargeDate } from '../lib/next-charge';
import { COMPANY, HAS_TELEGRAM } from '../lib/company';
import {
  cardsCreate,
  cardsGetVerifyCode,
  cardsVerify,
  type TokenizedCard,
} from '../lib/payme-subscribe-client';
import { claimOtpCooldown, saveCard, setAutoRenew, subscribeWithCard } from '../lib/billing-client';
import { ApiError } from '../lib/api';
import { gatewayMessage } from '../lib/gateway-message';
import { track } from '../lib/funnel';
import type { CheckoutField, CheckoutStep } from '../lib/checkout-events';
import { sendPurchaseConversionNow } from '../lib/purchase-conversion';
import { useSwipeDownToDismiss, type DismissVia } from '../lib/use-swipe-down-to-dismiss';
import { useBackClosesSheet } from '../lib/use-back-closes-sheet';
import {
  centreInScroller,
  useVisualViewportSheetHeight,
} from '../lib/use-visual-viewport-sheet-height';
import { useCheckoutSheetPresence } from '../lib/checkout-presence';
import { nicheHintLine } from '../lib/niche-hint';
import { CheckoutTermsLine } from './checkout-terms-line';

interface CardSaveModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after the card is saved (attach) or the subscription is created. */
  onSaved: () => void;
  /**
   * 'attach' (default): the user already paid — just save the card + enable
   * auto-renew. 'subscribe': charge the first period now via `planKey` and turn
   * on auto-renew in one step.
   */
  mode?: 'attach' | 'subscribe';
  /** Required when mode === 'subscribe'. */
  planKey?: string;
  /** subscribe mode: plan name shown in the summary (e.g. «Pro — месяц»). */
  planName?: string;
  /** subscribe mode: what will be charged now, formatted (e.g. «399 000 сум»). */
  priceLabel?: string;
  /**
   * subscribe mode: 'monthly' | 'annual'. Needed to say WHEN the next charge
   * lands. The window disclosed «далее — автопродление» and stopped there — no
   * amount, no interval — which is the first question anyone leaving a card on
   * file actually has.
   */
  billingPeriod?: string;
  /**
   * subscribe mode only: the dedup id for the begin-checkout event, minted by
   * the caller when the «Подписаться» button was clicked (where the browser Pixel
   * InitiateCheckout already fired). Threaded to the server so its CAPI
   * InitiateCheckout dedups against that browser event.
   */
  metaEventId?: string;
  /**
   * attach mode only: when true, save the card WITHOUT touching auto-renew (a
   * plain card change). Default false — attaching a card opts into auto-renew.
   */
  keepAutoRenew?: boolean;
  /** Payme/Octobank method toggle, rendered under the header — passed only for
   *  OCTO-allowlisted users who can switch to the Octobank redirect flow. */
  methodTabs?: import('react').ReactNode;
  /**
   * subscribe mode: switch the checkout to Octobank. Provided only when OCTO is
   * offered to this user (the same gate as `methodTabs`). Used to turn a dead
   * end — Payme REFUSING a Visa/Mastercard card at tokenization — into a
   * one-tap recovery, since Octobank clears Visa/Mastercard. Without it the
   * buyer met «Uzcard/Humo only» and left; we caught them by email a day later.
   * (Since 2026-09-09 Visa/MC are no longer rejected client-side, so the offer
   * triggers on Payme's own `payme_<code>` verdict, not on the prefix.)
   */
  onSwitchToOcto?: () => void;
  /**
   * subscribe mode: a live count of what the buyer's own niche is worth right
   * now, shown as a value pill under the price. `tier` decides the wording
   * (`niche` = open lots in their saved-search subject, `direction` = new lots
   * a month, `daily` = the whole live catalogue's daily pace when we know
   * nothing about them). Absent → no pill.
   */
  nicheHint?: { tier: 'niche' | 'direction' | 'daily'; count: number };
  /** Where the T-7 renewal reminder will go — names the channel in the terms
   *  line («по Telegram» / «по почте»), the owner's wording of 2026-09-24. */
  reminderChannel?: 'telegram' | 'email';
  /**
   * GUEST checkout — the ONE thing a buyer without an account sees that a
   * signed-in one does not: an email field above the payment-method strip, and
   * the `/auth/start` round trip the single primary button takes before it pays.
   * Built by `useGuestIdentify` in /plans, which also owns the method strip, so
   * picking Octobank can identify first and then hand over to the Octo sheet.
   *
   * ⚠️ Absent for a signed-in buyer, and EVERY branch that reads it is guarded
   * by `?.`, so the authed sheet renders and behaves exactly as it did before
   * this prop existed: no field, no extra round trip, no changed button, no
   * changed event. The hook latches its own guest flag, so `/auth/start`
   * flipping the session mid-sheet cannot pull the field out from under the
   * buyer.
   */
  identify?: import('./guest-checkout-identify').GuestIdentify;
  /**
   * The buyer put a digit in the card NUMBER field — the first one, once per
   * mount.
   *
   * /plans fires the Meta InitiateCheckout here instead of on the sheet's
   * opening FOR A GUEST (2026-09-22), and AddPaymentInfo here for EVERYONE
   * (2026-09-23) — a member's IC still fires on «Подписаться», so this is the
   * only place their "started paying" can be observed.
   * Opening a payment sheet is what a guest with no account does out of
   * curiosity — 1 of 31 such opens converted on 19–21.09 against 11 of 86 for
   * members — and Meta's CBO campaign optimises on that event. Typing a card
   * number is not curiosity.
   *
   * ⚠️ The NUMBER field, deliberately, not the address above it: the address is
   * how a guest identifies and it is typed by everyone who reads the sheet. And
   * not the expiry either — a browser that autofills the card fills the number
   * first, so the number is both the earliest and the only unambiguous signal.
   *
   * Absent for a signed-in buyer, whose IC fired on «Подписаться» as it always
   * has.
   */
  onPaymentEntryStarted?: () => void;
  /**
   * Keep what is typed when this sheet is re-OPENED without being unmounted.
   *
   * The reset further down exists because, for a signed-in buyer, closing the
   * sheet IS abandoning: the component stays mounted while closed, so the next
   * open must start clean or the buyer lands back on a stale OTP. That reasoning
   * does not survive the guest layout, where the sheet is unmounted outright on
   * close (`{guestSheetOpen && …}`) and the ONLY way `open` can go false→true is
   * the buyer tapping Octobank and then Payme again. Wiping a correctly typed
   * card because someone looked at the other gateway is not a fresh attempt, it
   * is losing their work — and the address beside it DOES survive (it lives in
   * the plan card), so the sheet would come back half-filled.
   *
   * ⚠️ Default false. Absent for a signed-in buyer, whose reset is unchanged.
   */
  preserveOnReopen?: boolean;
  /**
   * Play the bottom-sheet entrance (fade + slide up from off-screen) on mount.
   *
   * ⚠️ FALSE for a gateway swap (Igor, 2026-09-20: Octobank → Payme «как будто
   * другую модалку вызывает»). The two bodies are separate components rendered
   * on `payMethod`, so a swap unmounts one and mounts the other — and only this
   * one has an entrance. Payme → Octobank therefore replaced the panel in a
   * single frame, while Octobank → Payme showed a bare backdrop for 200 ms and
   * then slid a sheet up from the bottom edge: the same swap, told as two
   * different stories, one of which reads as "a different modal opened".
   *
   * Symmetry is what is being fixed, not the animation itself: a FIRST open
   * still animates, exactly as before. `OctoSubscribeModal` has never had an
   * entrance, so matching it means dropping this one for the swap rather than
   * adding one over there — the alternative makes both directions replay a full
   * slide-up for a radio-button press.
   */
  enterAnimation?: boolean;
}

/**
 * Payme's own wording for a card failure, or null if this is not one.
 *
 * Checks for the numeric `code` that `PaymeCardError` carries rather than
 * `instanceof` that class. Deliberate: this component's Payme client is mocked
 * in its own unit tests, and a mock factory that omits the class makes
 * `instanceof` throw a TypeError *inside the catch block* — swallowing the
 * error it was meant to describe. Production code should not depend on a class
 * identity that tests routinely replace, and the property is what actually
 * matters here: did Payme send us something worth showing.
 */
/** Payme's corporate-card refusal, in the three languages its `message` comes in. */
const CORPORATE_CARD_RE = /корпоратив|korporativ|corporate/i;

function paymeMessage(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const code = (err as Error & { code?: unknown }).code;
  if (typeof code !== 'number') return null;
  const msg = err.message.trim();
  return msg.length > 0 ? msg : null;
}

/**
 * The `reason` written to `checkout_failed` for a card-flow error.
 *
 * `PaymeCardError` (payme-subscribe-client) carries a NUMERIC `code` and is NOT
 * an `ApiError`, so the old `err instanceof ApiError ? … : 'network'` filed every
 * Payme decline — blocked card, wrong number, expired, no funds — under
 * `network`, the same bucket as a real disconnect. That made the single most
 * important signal on the paid funnel unreadable. Duck-type on the numeric code,
 * exactly as `paymeMessage` above does and for the same reason (a mock factory
 * that omits the class makes `instanceof` throw inside the catch). A numeric code
 * becomes `payme_<code>`; a string-coded `ApiError` passes through; `network` is
 * left to mean only a genuine transport failure.
 */
function failureReason(err: unknown): string {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  if (typeof code === 'number') return `payme_${code}`;
  if (err instanceof ApiError) return err.code ?? 'api_error';
  return 'network';
}

/**
 * What a refused CHARGE meant → our own line, per locale. Keyed by `cause`,
 * which the API reads out of Payme's SENTENCE (`charge-failure.ts`,
 * `paymeCause`) — NOT by the code: Payme's own error sheet lists `-31630` on
 * `receipts.pay` against seventeen different texts, from «недостаточно
 * средств» to «карта корпоративная», so a code-keyed map told a buyer with no
 * money and one with a corporate card the same thing. A cause we do not have
 * a line for falls through to the gateway's wording or the generic line.
 */
const CHARGE_CAUSE_COPY: Record<string, string> = {
  insufficient_funds: 'billing.card.errorInsufficientFunds',
  corporate_card: 'billing.card.errorCorporateCard',
  card_blocked: 'billing.card.errorCardBlocked',
  card_expired: 'billing.card.errorCardExpired',
  sms_not_enabled: 'billing.card.errorSmsNotEnabled',
  wrong_expiry: 'billing.card.errorWrongExpiry',
  wrong_number: 'billing.card.errorWrongNumber',
  card_not_found: 'billing.card.errorCardNotFound',
  card_not_served: 'billing.card.errorCardNotServed',
  limit_exceeded: 'billing.card.errorLimitExceeded',
  currency: 'billing.card.errorCurrency',
  processing_unavailable: 'billing.card.errorProcessingUnavailable',
};

/**
 * `cards.verify` refusals that are NOT «wrong code», by Payme's numeric code —
 * unambiguous here, unlike the charge codes: the sheet gives -31101 exactly one
 * meaning on this method («время жизни кода истекло») and -31102 exactly one
 * («количество попыток превышено»). Both used to print «Неверный код» — a
 * buyer who typed the right code from an expired SMS was told it was wrong.
 */
const OTP_CODE_COPY: Record<number, string> = {
  [-31101]: 'billing.card.errorOtpExpired',
  [-31102]: 'billing.card.errorOtpAttempts',
};

const TITLE_ID = 'card-save-title';

/**
 * How long before the OTP can be re-requested, from the moment a code is sent.
 *
 * Two figures since 2026-10-06 (one 45 s before): the FIRST resend opens at
 * 20 s, every later one at 60 s. Measured over the 30 days before: half the
 * codes arrive within 16 s and 90 % within 34 s, and of the 24 buyers who
 * closed the sheet on this step, half left inside 21 s — before the old
 * countdown ever offered a way out. 20 s sits just past the median arrival: a
 * resend earlier than that hands the buyer two codes while the first SMS is
 * still landing, and the code he then types is the stale one («неверный
 * код», the 2026-09-23 trace). The longer wait afterwards keeps a nervous
 * thumb from stacking a third and fourth code the same way. The server gate
 * (`OTP_COOLDOWN_SECONDS`, 15 s) must stay SHORTER than the first figure, or
 * the first legitimate resend would be refused as a double press.
 *
 * ⚠️ Since 2026-10-17 these are only the FALLBACK. Payme's `get_verify_code`
 * answers with its own `wait` (60 000 ms in practice), and a request inside it
 * sends nothing: the owner's resend at 22 s brought no SMS, the one at 1:45
 * brought the code again. A countdown shorter than Payme's offers a button
 * that does nothing, so `resendSeconds` takes Payme's figure when it has one.
 */
const RESEND_FIRST_S = 20;
const RESEND_NEXT_S = 60;

/**
 * The countdown before the next resend: Payme's `wait` when it sent one, never
 * under `RESEND_FIRST_S` (our server gate must still fit inside it) nor over
 * three minutes (a wild value must not lock the buyer out); `fallback` when
 * Payme said nothing.
 */
function resendSeconds(waitMs: number | undefined, fallback: number): number {
  if (!waitMs) return fallback;
  return Math.min(Math.max(Math.ceil(waitMs / 1000), RESEND_FIRST_S), 180);
}

type Scheme = 'uzcard' | 'humo';

/** Every card brand this form can recognise. Only `uzcard`/`humo` get a logo
 *  in the scheme strip — `visa`/`mastercard` simply show no domestic logo
 *  highlighted, but are otherwise accepted (see `detectBrand`). */
type Brand = 'uzcard' | 'humo' | 'visa' | 'mastercard';

/**
 * Detect the card brand from the leading digits. Needs ≥ 4 digits to be sure.
 *
 * Payme's Subscribe API (`cards.create`) does not restrict scheme, and the
 * browser calls it DIRECTLY — the PAN never touches our server — so rejecting
 * a card client-side costs real signups for zero benefit: Payme's own error
 * code is the authoritative verdict (see `failureReason`). `numberProblem`
 * below only flags 'unsupported' when this returns null, i.e. the prefix
 * matches no brand at all — not merely "not a domestic card".
 *
 * `6262` is the Uzcard·UnionPay co-badge (same physical card, same logo as a
 * plain Uzcard) — prod measured 12 real declines against exactly this prefix
 * before it was added here.
 */
function detectBrand(digits: string): Brand | null {
  if (digits.length < 4) return null;
  if (digits.startsWith('8600') || digits.startsWith('5614') || digits.startsWith('6262')) {
    return 'uzcard';
  }
  if (digits.startsWith('9860')) return 'humo';
  if (digits.startsWith('4')) return 'visa';
  const twoDigit = Number(digits.slice(0, 2));
  if (twoDigit >= 51 && twoDigit <= 55) return 'mastercard';
  const fourDigit = Number(digits.slice(0, 4));
  if (fourDigit >= 2221 && fourDigit <= 2720) return 'mastercard';
  return null;
}

/** The domestic-only subset of `detectBrand`, for the logo strip / OTP recap —
 *  those only ever show a Uzcard or Humo mark. */
function detectScheme(digits: string): Scheme | null {
  const brand = detectBrand(digits);
  return brand === 'uzcard' || brand === 'humo' ? brand : null;
}

/**
 * Luhn checksum. A 16-digit number with a recognised brand prefix can still be
 * mistyped — this catches that case as `'invalid'` (a client-side fact, not a
 * guess) instead of letting a doomed tokenize call go out to Payme.
 */
function luhnValid(digits: string): boolean {
  let sum = 0;
  let alternate = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = Number(digits[i]);
    if (alternate) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alternate = !alternate;
  }
  return sum % 10 === 0;
}

/** Group the PAN into 4-digit blocks for display: `8600 1234 5678 9012`. */
function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Normalise whatever landed in the expiry field to bare `MMYY`.
 *
 * ⚠️ Autofill does NOT hand us `MM/YY`. `autocomplete="cc-exp"` yields
 * `06/2029` on Chrome and Safari, `062029` on some Android keyboards, and the
 * HTML `month` serialisation `2029-06` on some builds. The old mask stripped
 * non-digits and kept the FIRST FOUR, so
 *   `06/2029` → `0620` → a valid card was told it expired in 2020 (Igor's own
 *   Mastercard on 2026-09-15, the one `checkout_failed{reason:'expired'}` row);
 *   `2029-06` → `2029` → month clamped to 12 → `12/29`, silently the wrong month.
 * Normalise the year BEFORE truncating, never after.
 */
export function normalizeExpiryDigits(value: string): string {
  const raw = value.trim();
  // ISO `YYYY-MM` — matched on the RAW string: once the hyphen is gone,
  // `202906` and `062029` are the same six digits in a different order.
  const iso = /^(\d{4})-(\d{1,2})$/.exec(raw);
  if (iso) return `${iso[2].padStart(2, '0')}${iso[1].slice(2)}`;
  const digits = raw.replace(/\D/g, '');
  // `MMYYYY` — only when the tail really looks like a year. The field is
  // controlled and re-masked on every keystroke, so six digits can only come
  // from autofill or a paste; the year check keeps a stray paste as-is.
  if (digits.length === 6 && Number(digits.slice(2)) >= 2000) {
    return `${digits.slice(0, 2)}${digits.slice(4)}`;
  }
  return digits.slice(0, 4);
}

/** Format expiry as `MM/YY` with a light month clamp (`5` → `05`, `13+` → `12`). */
export function formatExpiry(value: string): string {
  let digits = normalizeExpiryDigits(value);
  if (digits.length === 1 && Number(digits) > 1) digits = `0${digits}`;
  if (digits.length >= 2) {
    const mm = Number(digits.slice(0, 2));
    if (mm === 0) digits = `01${digits.slice(2)}`;
    else if (mm > 12) digits = `12${digits.slice(2)}`;
  }
  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

type NumberProblem = 'incomplete' | 'unsupported' | 'invalid';
type ExpiryProblem = 'invalid' | 'expired';

function numberProblem(digits: string): NumberProblem | null {
  if (digits.length === 0) return null;
  // 'unsupported' means the prefix matches NO brand at all — not merely
  // "not domestic". Visa/Mastercard pass; Payme's own decline is authoritative.
  if (digits.length >= 4 && !detectBrand(digits)) return 'unsupported';
  if (digits.length < 16) return 'incomplete';
  return luhnValid(digits) ? null : 'invalid';
}

function expiryProblem(digits: string): ExpiryProblem | null {
  if (digits.length === 0) return null;
  if (digits.length < 4) return 'invalid';
  const mm = Number(digits.slice(0, 2));
  const yy = Number(digits.slice(2, 4));
  if (mm < 1 || mm > 12) return 'invalid';
  const now = new Date();
  const curYY = now.getFullYear() % 100;
  const curMM = now.getMonth() + 1;
  if (yy < curYY || (yy === curYY && mm < curMM)) return 'expired';
  return null;
}

/**
 * Focus a field the submit just rejected AND bring it into view inside the
 * sheet's own scroller.
 *
 * Focusing alone is not enough any more. The pay button sits in a pinned footer
 * and the body beside it is what scrolls, so a buyer who has scrolled down to
 * the button can press it with the card number well above the visible area:
 * the field takes the caret, prints «Введите номер карты», and none of that is
 * on screen. That is the hole the duplicated footer alert was papering over.
 *
 * ⚠️ `preventScroll` + `centreInScroller`, never `scrollIntoView` — the same
 * rule `use-visual-viewport-sheet-height.ts` documents at length, and now
 * implemented in exactly one place: letting the browser scroll a focused field
 * also lets iOS pan the LAYOUT viewport, which shifts the `position: fixed`
 * overlay the sheet lives in. Centring the field keeps its label above it and
 * its error line below it visible.
 */
function revealField(el: HTMLElement | null, scroller: HTMLElement | null): void {
  if (!el) return;
  el.focus({ preventScroll: true });
  centreInScroller(el, scroller);
}

/** Seconds → `M:SS` for the resend countdown (42 → «0:42», 65 → «1:05»). */
function formatMmss(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * The SMS code: ONE real input drawn as six boxes.
 *
 * Until 2026-10-17 these were six separate inputs with `one-time-code` on the
 * first. iOS Safari read the code in the SMS (it was underlined in Messages)
 * and still never offered it above the keyboard — it offered the saved CARD
 * instead (owner's iPhone, 2026-10-17). One input with `one-time-code` is the
 * shape iOS and Android autofill reliably, so the input is real and full-size,
 * laid over the boxes with transparent text and caret; the boxes only draw its
 * digits and where the next one goes. Typing, deleting, pasting and autofill
 * are the browser's own: no auto-advance to get wrong.
 */
function OtpPins({
  value,
  onChange,
  disabled,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  /** Accessible label of the input (also where `one-time-code` lives). */
  label: string;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = useState(false);
  const active = Math.min(value.length, 5);

  // Focus the input once, the moment the step appears.
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div className="relative mx-auto w-max">
      <div className="flex justify-center gap-2" aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={i}
            className={`flex h-12 w-11 items-center justify-center rounded-lg border bg-bone-surface text-[20px] font-bold text-ink ${
              focused && i === active ? 'border-brand' : 'border-edge'
            } ${disabled ? 'opacity-60' : ''}`}
          >
            {value[i] ?? ''}
          </div>
        ))}
      </div>
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label={label}
        maxLength={12}
        disabled={disabled}
        value={value}
        // iOS has been seen to insert an autofilled code twice: keep six digits.
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        onFocus={(e) => {
          setFocused(true);
          // The caret goes to the end — the code is typed and erased from there.
          const el = e.currentTarget;
          el.setSelectionRange(el.value.length, el.value.length);
        }}
        onBlur={() => setFocused(false)}
        // ≥ 16 px or iOS zooms the page on focus; the text itself is invisible.
        className="absolute inset-0 h-full w-full bg-transparent text-[16px] text-transparent caret-transparent outline-none selection:bg-transparent"
      />
    </div>
  );
}

/**
 * Two-step card-save flow for auto-renewal. Card number + expiry are tokenized
 * DIRECTLY with Payme in the browser (raw PAN never hits our server); we then
 * OTP-verify and post only the verified token to our API. The `<form>` has no
 * `action` and the inputs no `name` (Payme requirement — nothing is ever
 * submitted to our own server as form data).
 *
 * Chrome mirrors OnboardingModal (the app's modal convention): a bottom sheet on
 * mobile, a centered card on desktop, in the bone/ink/brand/edge vocabulary — NOT
 * the shadcn Dialog primitives, whose cream/charcoal/pill palette read as foreign
 * against the rest of the app.
 */
export function CardSaveModal({
  open,
  onClose,
  onSaved,
  mode = 'attach',
  planKey,
  planName,
  priceLabel,
  billingPeriod,
  metaEventId,
  methodTabs,
  onSwitchToOcto,
  nicheHint,
  reminderChannel = 'email',
  keepAutoRenew = false,
  identify,
  onPaymentEntryStarted,
  preserveOnReopen = false,
  enterAnimation = true,
}: CardSaveModalProps) {
  const t = useT();
  const { locale } = useLocale();
  const [step, setStep] = useState<'card' | 'otp'>('card');
  /** Has this MOUNTED sheet ever been opened? Only `preserveOnReopen` reads it. */
  const openedOnce = useRef(false);

  /*
   * The exact date of the first auto-renewal, and how we will warn about it.
   *
   * «далее — автопродление» named a mechanism; a buyer wants a DATE. Computed
   * with the server's own arithmetic (see nextChargeDate) so the two cannot
   * disagree at month ends, and the reminder lead time is read from the same
   * constant the scheduler uses rather than typed in.
   *
   * The channel is branched, not assumed: the reminder goes to Telegram only
   * for a linked account and falls back to email otherwise
   * (`renewal-reminders.ts` → `channel: chatId ? 'telegram' : 'email'`).
   * Promising Telegram to someone who has not linked it would be a promise
   * broken by the very message meant to keep it.
   */
  const nextChargeAt = mode === 'subscribe' && billingPeriod ? nextChargeDate(billingPeriod) : null;
  const nextCharge = nextChargeAt ? formatDeadline(nextChargeAt.toISOString(), locale) : null;
  const [number, setNumber] = useState('');
  const [expire, setExpire] = useState('');
  const [code, setCode] = useState('');
  // Inline errors only surface once a field has been left / interacted with, so
  // the form doesn't scream at a half-typed number.
  const [touched, setTouched] = useState<{ number: boolean; expire: boolean }>({
    number: false,
    expire: false,
  });
  const [pending, setPending] = useState<TokenizedCard | null>(null);
  // The masked phone Payme SMS'd the code to, when it tells us (else null → a
  // generic "sent to the number on the card" line on the confirm step).
  const [phone, setPhone] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Payme has REFUSED the Visa/Mastercard now in the field (as opposed to the
  // field merely holding one). It promotes the red notice under the number from
  // the warning to the verdict; the one-tap Octobank switch is inside that same
  // notice either way. Reset per attempt, per open, and on any edited digit.
  const [octoOffer, setOctoOffer] = useState(false);
  /*
   * Payme refused a CORPORATE card (its own −31300 verdict, message
   * «Финансовые операции с корпоративных карт запрещены»): 10 people over the
   * 30 days to 2026-09-23 met one of the −31300 refusals. A corporate Uzcard/
   * Humo has the same one-tap recovery a Visa has — the Octobank switch — so
   * the same notice offers it (Igor, 2026-09-23: «если корп карта — скидывать
   * на Октобанк»). Retired the moment the number is edited, like `octoOffer`.
   */
  const [corporateOffer, setCorporateOffer] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  /**
   * The sheet's SCROLLING body. The panel is a flex column whose last row is
   * the sticky CTA footer, so the panel itself never scrolls and its
   * `scrollTop` is always 0 — handing this to `useSwipeDownToDismiss` is what
   * keeps a downward drag in the grab zone from dismissing a sheet the buyer
   * has merely scrolled down.
   */
  const bodyRef = useRef<HTMLDivElement>(null);
  /**
   * The pay/confirm button sits in the footer, OUTSIDE the `<form>` that owns
   * the fields, and is re-attached with `form=`. Ids are per-instance because
   * /plans mounts a guest sheet and an authed sheet in the same tree.
   */
  const formUid = useId();
  const cardFormId = `card-save-card-${formUid}`;
  const otpFormId = `card-save-otp-${formUid}`;
  /** Focus jumps here once 16 valid digits are in — see the effect below. */
  /*
   * Set on the first «Оплатить» tap that fails validation. An EMPTY field has no
   * `numberProblem` (typing must not be nagged from the first keystroke), so
   * before this flag a tap on the button with nothing entered showed nothing at
   * all — 128 `checkout_failed{reason:'empty_number'}` rows from 27 buyers since
   * 28.08, one iPhone buyer 19 taps in a row on 14.09 (who then paid from a Mac),
   * another 23 taps on 16.09. The button looked dead. This turns the tap into a
   * visible «Введите номер карты» + focus on the field.
   */
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const numberRef = useRef<HTMLInputElement>(null);
  const expireRef = useRef<HTMLInputElement>(null);
  /*
   * Seconds until «отправить код ещё раз» becomes available.
   *
   * Without it the OTP step is a dead end for anyone whose SMS does not arrive:
   * the funnel already distinguishes `otp_requested` from `otp_submitted`
   * precisely because people get stuck between them, and until now the only
   * exit was closing the sheet and starting over.
   */
  const [resendIn, setResendIn] = useState(0);
  /** A resend is on the wire — the control shows a spinner and refuses a press. */
  const [resending, setResending] = useState(false);
  /*
   * The code on screen can never pass again: Payme answered −31101 (expired)
   * or −31102 (attempts exhausted). `checkout_failed`, 30 days to 2026-10-16:
   * −31101 19 rows from 11 visitors, −31102 5 from 5 — and the confirm button
   * stayed live, so every press re-sent the same dead code. While this is set the
   * footer button IS the resend — «Новый код через 0:25» while Payme's wait
   * runs, then «Отправить код ещё раз» — so the one next move sits where the
   * thumb is (owner, 2026-10-17: «попытки исчерпаны, запросите новый код — а
   * запросить не могу»). A successful resend clears it. ONLY those two codes
   * set it — a wrong code (−31103), a network failure or a refused charge
   * never do.
   */
  const [otpDead, setOtpDead] = useState(false);
  /**
   * Put the caret back in the first box once the boxes are enabled again —
   * after a wrong code (−31103) they are cleared, and the first box is the one
   * iOS offers the code from Messages in. Read by the effect below; a ref so
   * setting it does not render by itself.
   */
  const refocusCode = useRef(false);

  /*
   * SINGLE FLIGHT for the two paths that ask for an SMS code.
   *
   * REFS, not `loading`/`resending`, and that is the whole point: state is only
   * visible to the NEXT render, so two presses inside one frame both read
   * `loading === false` and both fire. A ref flips synchronously, on the first
   * press, before the second handler runs.
   *
   * Traced on prod 2026-09-23: two SMS four seconds apart, then «неверный код»
   * — the buyer typed the code from the FIRST message while the sheet had
   * already moved on to the second token. A second request does not just waste
   * an SMS; it invalidates the code the buyer is looking at.
   */
  const cardSubmitInFlight = useRef(false);
  const resendInFlight = useRef(false);

  // Guards against an accidental dismiss when a press begins inside the modal
  // and releases on the backdrop (mirrors OnboardingModal).
  const overlayPress = useRef(false);

  /*
   * Funnel instrumentation for the checkout black box.
   *
   * Everything between «Подписаться» and a completed charge happens in this
   * component and against Payme directly, so the server saw nothing: prod held
   * 114 pending orders with no `payme_create_time` at all — not one reached a
   * Payme transaction — and no event said why. `checkoutId` (the metaEventId
   * minted on the click) ties these to the server's `checkout_started`.
   */
  const attempts = useRef({ card: 0, otp: 0 });
  /** Guards `checkout_field_completed` to one row per field per modal open. */
  const fieldsDone = useRef({ number: false, expiry: false });
  /**
   * Which fields have already written `checkout_field_started`, and for WHICH
   * checkout.
   *
   * Keyed on `checkoutId` rather than on the sheet opening, because a guest's
   * gateway swap (Payme → Octobank → Payme) re-opens this sheet with the card
   * preserved and the SAME id — one checkout, not two, exactly as
   * `paymentEntryStarted` treats it. A new id re-arms all three fields.
   *
   * In attach mode there is no `checkoutId` at all (nothing is being bought),
   * so the guard there is per MOUNT, and the row carries no `checkoutId` —
   * the same shape `checkout_field_completed` already has on that path.
   */
  const fieldsStarted = useRef<{
    id: string | undefined;
    number: boolean;
    expiry: boolean;
    otp: boolean;
  }>({ id: undefined, number: false, expiry: false, otp: false });
  /** How many card digits there were on the previous render. The auto-advance
   *  fires on the step UP TO sixteen and never on an edit within a complete
   *  number — see the effect. */
  const numberLenBefore = useRef(0);
  /**
   * `onPaymentEntryStarted` has already been called for this MOUNT.
   *
   * Per mount, not per open, and deliberately NOT reset with the form below: a
   * guest's gateway swap (Payme → Octobank → Payme) re-opens this sheet with
   * the card preserved, and that is one checkout, not two. /plans dedups by
   * `metaEventId` on top of this, so a doubled call could not reach Meta
   * twice — but it must not reach it twice from here either.
   */
  const paymentEntryStarted = useRef(false);
  const stepEnteredAt = useRef(Date.now());
  // Suppresses the dismiss event when the modal closes because it SUCCEEDED —
  // otherwise every completed purchase also books an abandonment.
  const completed = useRef(false);

  /*
   * Close WITHOUT completing — the abandonment signal.
   *
   * Without it, "typed nothing and left" is indistinguishable from "still
   * typing", which is the difference between a broken step and a slow one.
   * Carries which step they were on and how long they sat there, because the
   * useful question is not how many abandon but WHERE.
   */
  const dismiss = useCallback(
    (via: DismissVia = 'close') => {
      if (!completed.current) {
        track('checkout_dismissed', {
          checkoutId: metaEventId,
          plan: planKey,
          step,
          msInStep: Date.now() - stepEnteredAt.current,
          // `attempt`, singular — the same key every other rung uses for the same
          // number. It was `attempts` here, so a breakdown had to know which
          // event it was reading.
          attempt: step === 'card' ? attempts.current.card : attempts.current.otp,
          // WHO closed it: a swipe (a gesture, often accidental — 2026-09-17), the
          // ✕, a backdrop tap or Escape. Without this the 24% of mobile card
          // checkouts dismissed untouched within 15 s could not be told apart
          // from a buyer who read the price and left on purpose.
          via,
        });
      }
      onClose();
    },
    [onClose, step, metaEventId, planKey],
  );

  /*
   * The FIRST character in a field — the step below `checkout_field_completed`.
   *
   * `_completed` only fires once a field is VALID, so a half-typed card number
   * is indistinguishable from an untouched one: measured over the 25 days to
   * 2026-09-25, 235 people never reached a full number and nothing said whether
   * they tried. Those are opposite diagnoses — a mask or keyboard that will not
   * accept the digits, versus a buyer who read the price and stopped — and the
   * ratio `_started` / `_completed` per field is what separates them.
   *
   * Gated on a real DIGIT in the field's NEW value, read off the event rather
   * than the state the handler is still setting: a pasted «—», a cleared field
   * or a formatter rewriting the same value must not count as typing. Same rule
   * as the guest `AddPaymentInfo` gate on the number field.
   */
  const startField = useCallback(
    (field: CheckoutField, value: string) => {
      if (!/\d/.test(value)) return;
      if (fieldsStarted.current.id !== metaEventId) {
        fieldsStarted.current = { id: metaEventId, number: false, expiry: false, otp: false };
      }
      if (fieldsStarted.current[field]) return;
      fieldsStarted.current[field] = true;
      track('checkout_field_started', { checkoutId: metaEventId, plan: planKey, field });
    },
    [metaEventId, planKey],
  );

  // Swipe the mobile bottom sheet down to dismiss. `setPanelRef` is stable and
  // fans the node out to BOTH the focus/scroll ref and the gesture ref.
  const swipe = useSwipeDownToDismiss(dismiss, open, bodyRef);
  // The phone's Back closes the sheet, not the page (Igor, 10.10) — counted
  // as `checkout_dismissed{via:'back'}`.
  useBackClosesSheet(open, () => swipe.dismiss('back'));
  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;
      swipe.ref.current = node;
    },
    [swipe.ref],
  );

  // Full reset each time the modal opens — a fresh subscribe intent starts over
  // at the card step, never mid-OTP with a stale token/code.
  //
  // This replaces the old "keep progress across close/reopen" behaviour, which
  // preserved the STEP too: closing the sheet on the OTP screen and re-clicking
  // the CTA dropped the buyer straight back onto a stale OTP (old code, possibly
  // expired token). Closing = abandoning, so the next open is clean. Nothing is
  // persisted anywhere (a PAN is never written to storage); this only clears the
  // in-memory state the component held while open.
  useEffect(() => {
    if (!open) return;
    // GUEST gateway swap, not a fresh attempt — see `preserveOnReopen`. The ref
    // makes the FIRST open still initialise; only re-opens are spared.
    if (preserveOnReopen && openedOnce.current) return;
    openedOnce.current = true;
    setStep('card');
    setNumber('');
    setExpire('');
    setCode('');
    setPending(null);
    setPhone(null);
    setTouched({ number: false, expire: false });
    setSubmitAttempted(false);
    setResendIn(0);
    setResending(false);
    setOtpDead(false);
    cardSubmitInFlight.current = false;
    resendInFlight.current = false;
    setError(null);
    // …and the refusal the number field is showing. It drives that notice's
    // wording now, so a leftover one would open a fresh sheet already saying
    // «эту карту Payme не принял» about a field with nothing in it.
    setOctoOffer(false);
    setCorporateOffer(false);
    setLoading(false);
    fieldsDone.current = { number: false, expiry: false };
    numberLenBefore.current = 0;
    attempts.current = { card: 0, otp: 0 };
    stepEnteredAt.current = Date.now();
    completed.current = false;
  }, [open]);

  // Tick the resend cooldown down to zero while the OTP step is showing.
  useEffect(() => {
    if (step !== 'otp' || resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [step, resendIn]);

  // After a wrong code: caret into the (cleared) first box once `loading` has
  // re-enabled it — a disabled input refuses focus, so this cannot happen in
  // the submit handler itself.
  useEffect(() => {
    if (!refocusCode.current || loading || step !== 'otp') return;
    refocusCode.current = false;
    document
      .getElementById(otpFormId)
      ?.querySelector<HTMLInputElement>('input')
      ?.focus({ preventScroll: true });
  }, [loading, step, otpFormId]);

  // Page scroll behind the modal is locked by the `<RemoveScroll>` wrapper on
  // the return — the same iOS-correct lock Radix `Sheet` uses (a bare
  // `body{overflow:hidden}` fails to lock the background on iOS AND freezes the
  // sheet's own inner scroll there, which read as "can't scroll the payment bar").

  // Tell the app a checkout sheet is on screen, so `OnboardingModal` does not
  // open on top of it the instant `/auth/start` creates a guest's account —
  // which is exactly what happened before payment on 2026-09-20.
  useCheckoutSheetPresence(open);

  // Keyboard-safe sheet height — see the hook for the full iOS story.
  // ⚠️ `bodyRef`, not the panel: the panel stopped scrolling when the pay button
  // became a pinned footer, so the hook's focus-centring had nothing to move and
  // tapping the card number no longer brought it into view.
  useVisualViewportSheetHeight(open, bodyRef);

  // Restore focus to whatever opened the sheet — but ONLY when the sheet
  // closes. This used to live in the trap effect below, whose deps include
  // `dismiss`; `dismiss` is rebuilt whenever /plans hands down a new `onClose`,
  // so every parent re-render (a `['billing','methods']` refetch on window
  // focus, the subscribe-hint settling, the gateway strip) ran the cleanup and
  // yanked the caret out of the card field back to the «Подписаться» button —
  // on iOS that closes the keyboard mid-number (Igor, 2026-09-20). The guest
  // sheet hit the same thing through its email field and was patched by
  // memoizing its `onClose` in /plans; keying the restore on `open` alone
  // fixes it for every caller regardless of prop stability.
  const restoreFocusTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    return () => {
      restoreFocusTo.current?.focus?.();
      restoreFocusTo.current = null;
    };
  }, [open]);

  // Move focus in, Escape-to-close, and trap Tab within the dialog so keyboard
  // focus can't escape to the page behind.
  useEffect(() => {
    if (!open) return;
    const el = contentRef.current;
    // `preventScroll`: a programmatic focus otherwise lets iOS pan the layout
    // viewport under the fixed overlay; the sheet's own focus-centring (see
    // useVisualViewportSheetHeight) brings the field into view instead.
    // Only the OTP step auto-focuses. On the card step a phone keyboard would
    // pop instantly and cover the price, the dates and the trust block — the
    // buyer saw ~30% of the sheet before reading a word of it (Igor, 2026-09-09).
    if (step === 'otp') {
      el?.querySelector<HTMLElement>('input')?.focus({ preventScroll: true });
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        dismiss('escape');
        return;
      }
      if (e.key !== 'Tab' || !el) return;
      const f = el.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])',
      );
      if (f.length === 0) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open, dismiss, step]);

  const numberDigits = number.replace(/\D/g, '').slice(0, 16);
  const expireDigits = expire.replace(/\D/g, '').slice(0, 4);
  const codeDigits = code.replace(/\D/g, '').slice(0, 6);

  const numberIssue = numberProblem(numberDigits);
  const expireIssue = expiryProblem(expireDigits);
  const otpValid = codeDigits.length === 6;

  /*
   * Field-level progress — ONCE per field per modal open, when it first becomes
   * valid. Not on focus, blur or keystroke: at most two rows per checkout.
   *
   * This is the difference between "people are frightened off" and "people
   * cannot fill this in". If the number is completed and the expiry never is,
   * that is a broken expiry input, not hesitation — and on a form that has been
   * suspected on mobile before, that distinction is the whole question.
   */
  /*
   * Move to the expiry field on the SIXTEENTH DIGIT.
   *
   * On a phone this saves a deliberate tap on a 44px target while a numeric
   * keyboard covers half the screen — the single cheapest thing that can be
   * done for this form.
   *
   * ⚠️ Digits, NOT validity. This used to wait for `numberIssue === null`,
   * i.e. for Luhn AND a recognised brand — so a card whose prefix our own
   * `detectBrand` table does not know sat there with all sixteen digits typed
   * and the caret still in the number field, and the buyer had to find the
   * 112px expiry box himself (Igor, iPhone 15, 2026-09-21: «должно же автоматом
   * переключиться»). Whether the PAN is acceptable is Payme's verdict to give
   * on submit; sixteen digits is simply the end of the field.
   *
   * ⚠️ Only while the caret is IN the number. Autofill writes the number and
   * the expiry in one go, and dragging focus off a field the browser has just
   * filled is how «дёргает» starts.
   *
   * ⚠️ On the STEP UP to sixteen, never on an edit inside a number that was
   * already sixteen long. That distinction is what keeps the Payme refusal's
   * caret: a refused card puts the caret back in the number (`revealField` in
   * the tokenize catch) so the buyer reads the verdict on the field it is
   * about, and if a corrected digit then bounced focus to the expiry, the
   * number's `onBlur` would mark it `touched` and swap that verdict for a
   * plain «Неверный номер карты» — a press answered by LESS than it said
   * before. Deleting a digit and retyping it still advances: that is the
   * sixteenth digit being typed.
   *
   * `numberLenBefore` is the whole latch — a separate boolean would have to
   * agree with it, and `fieldsDone` cannot serve: it is only ever set for a
   * VALID number, so sharing it would re-fire this on every keystroke of a
   * card no brand table knows.
   *
   * Nothing auto-advances off the EXPIRY: the next step is an SMS the server
   * has not been asked for yet, so there is no field to move to.
   */
  useEffect(() => {
    const before = numberLenBefore.current;
    numberLenBefore.current = numberDigits.length;
    if (numberDigits.length !== 16 || before === 16) return;
    if (document.activeElement !== numberRef.current) return;
    // `preventScroll` — see the open-focus note: iOS otherwise pans the page
    // under the sheet on this auto-advance and the field lands off-screen.
    expireRef.current?.focus({ preventScroll: true });
  }, [numberDigits]);

  useEffect(() => {
    if (numberIssue === null && numberDigits.length === 16 && !fieldsDone.current.number) {
      fieldsDone.current.number = true;
      track('checkout_field_completed', {
        checkoutId: metaEventId,
        plan: planKey,
        field: 'number' satisfies CheckoutField,
      });
    }
  }, [numberIssue, numberDigits, metaEventId, planKey]);

  useEffect(() => {
    if (expireIssue === null && expireDigits.length === 4 && !fieldsDone.current.expiry) {
      fieldsDone.current.expiry = true;
      track('checkout_field_completed', {
        checkoutId: metaEventId,
        plan: planKey,
        field: 'expiry' satisfies CheckoutField,
      });
    }
  }, [expireIssue, expireDigits, metaEventId, planKey]);

  const numberErrorText =
    submitAttempted && numberDigits.length === 0
      ? t('billing.card.errorEmpty')
      : numberIssue === 'unsupported'
        ? t('billing.card.errorUnsupported')
        : numberIssue === 'incomplete'
          ? t('billing.card.errorIncomplete')
          : numberIssue === 'invalid'
            ? t('billing.card.errorInvalidNumber')
            : null;
  /*
   * The unsupported-prefix error surfaces as soon as it's KNOWN (≥ 6 digits —
   * past the point where the prefix is decided), not gated behind `touched`
   * like the other field errors. 313 prod `checkout_dismissed` events on this
   * step averaged 13.6 minutes in-step; a buyer typing a Visa/foreign card that
   * genuinely can't be recognised deserves the answer before they finish typing
   * all 16 digits and hit submit, not only after blurring away.
   */
  const showNumberError =
    numberErrorText != null &&
    (touched.number || (numberIssue === 'unsupported' && numberDigits.length >= 6));
  const expireErrorText =
    submitAttempted && expireDigits.length === 0
      ? t('billing.card.errorExpireEmpty')
      : expireIssue === 'expired'
        ? t('billing.card.errorExpired')
        : expireIssue === 'invalid'
          ? t('billing.card.errorExpireInvalid')
          : null;
  /*
   * Payme's Cards API takes Uzcard/Humo only. A Visa/Mastercard is deliberately
   * NOT blocked here — Payme's own decline stays the authoritative verdict (see
   * the tokenize catch below), and blocking on brand would strand a Visa holder
   * hitting a transient blip. But saying nothing means the Payme method card
   * above still reads «Uzcard · Humo» while a 5xxx PAN sits in the field, and
   * the buyer learns only after an SMS round trip (Igor, 2026-09-15: «показало
   * Uzcard, а карта Mastercard»). A non-blocking notice with the same one-tap
   * Octobank escape the decline already offers.
   */
  const typedBrand = numberDigits.length >= 6 ? detectBrand(numberDigits) : null;
  const foreignBrandTyped = typedBrand === 'visa' || typedBrand === 'mastercard';

  /**
   * Ask for the SMS code — the ONE path both call sites go through.
   *
   * Two steps, in this order:
   *
   *  1. `claimOtpCooldown` — OUR 15-second, per-card-token gate. It is a GATE
   *     and not a proxy: the code is requested by this browser straight from
   *     `checkout.paycom.uz`, because the card flow is SAQ A-EP and the PAN
   *     never reaches our server, so there is no server request to throttle.
   *     `{ cooldown: true }` means it refused — one is already on its way.
   *  2. `cardsGetVerifyCode` — Payme, which actually sends the SMS.
   *
   * ⚠️ FAILS OPEN. Anything from step 1 that is not a 429 — a 500, an offline
   * phone, our API down — is swallowed, because the code does not come from us
   * and a guard must never become a dependency on the one flow that takes
   * money. The refusal is the only answer worth obeying.
   */
  async function requestSmsCode(
    token: string,
  ): Promise<{ phone?: string; cooldown?: boolean; waitMs?: number }> {
    try {
      await claimOtpCooldown(token);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) return { cooldown: true };
    }
    // Payme omits `phone` on some accounts and the whole result on others, so
    // normalise to an object here and let both call sites stop guarding for it.
    const sent = await cardsGetVerifyCode(token);
    return { phone: sent?.phone, waitMs: sent?.waitMs };
  }

  async function handleCardSubmit(e: React.FormEvent) {
    e.preventDefault();
    // `loading` for the render the buyer can see, the ref for the frame he
    // cannot: a double tap fires both handlers before React re-renders.
    if (loading || cardSubmitInFlight.current) return;

    /*
     * Autofill recovery. Some card autofill — notably the Yandex Pay overlay a
     * buyer reported — writes a field's DOM value WITHOUT dispatching a React
     * change event, so `number`/`expire` state stay empty while the inputs look
     * filled. The buyer then meets a dead button and we log nothing. Read the
     * live DOM values on submit and take whichever is longer than state.
     */
    const domNumberRaw = numberRef.current?.value ?? '';
    const domNumber = domNumberRaw.replace(/\D/g, '').slice(0, 16);
    // Same normaliser as the mask — this path exists precisely FOR autofill, so
    // it must not re-introduce the `06/2029` → `0620` cut.
    const domExpire = normalizeExpiryDigits(expireRef.current?.value ?? '');
    const subNumber = domNumber.length > numberDigits.length ? domNumber : numberDigits;
    const subExpire = domExpire.length > expireDigits.length ? domExpire : expireDigits;
    if (subNumber !== numberDigits) setNumber(formatCardNumber(subNumber));
    if (subExpire !== expireDigits) setExpire(formatExpiry(subExpire));
    // Was the raw DOM value non-empty while the digits we're about to submit
    // are empty? Distinguishes "the field was truly blank" from "something was
    // typed/autofilled but produced no digits" (or React state never saw it) —
    // both currently collapse into the same `empty_number` reason.
    const domHadValue = domNumberRaw.length > 0;

    /*
     * GUEST ONLY. The address is checked (client-side, no request) on the SAME
     * tap as the card, so a typo in one and a gap in the other surface together
     * instead of one error per tap. True — a no-op — for a signed-in buyer,
     * whose `identify` prop is absent.
     */
    const emailOk = identify?.precheck() ?? true;

    const subNumberIssue = numberProblem(subNumber);
    const subExpireIssue = expiryProblem(subExpire);
    // `expiryProblem('')` is null on purpose (an untouched field is not yet
    // wrong), so emptiness has to be checked HERE as well as for the number.
    // Without the length check a full number with a blank expiry went straight
    // to `cards.create`, and Payme answered «Invalid Params» under the button
    // instead of our «Укажите срок» (Igor, 2026-09-17, first thing he tried).
    const subValid =
      subNumberIssue === null &&
      subExpireIssue === null &&
      subNumber.length === 16 &&
      subExpire.length === 4;

    attempts.current.card += 1;

    /*
     * A card that never passes client validation used to exit here in silence.
     * The submit button was `disabled` in that state, and a disabled button
     * fires no DOM events — no handler, no $autocapture, no $rageclick — so the
     * whole failure class was invisible, indistinguishable from someone who
     * changed their mind, and support answered «отказ на стороне Payme» for a
     * card Payme never saw. The button is clickable now; record WHY it refused
     * and reveal the inline error the buyer needs.
     */
    if (!subValid) {
      setTouched({ number: true, expire: true });
      setSubmitAttempted(true);
      /*
       * Put the caret where the problem is: an empty/short number, else the
       * expiry. On a phone the numeric keyboard opens on the right field.
       *
       * ⚠️ …unless the ADDRESS is also wrong. It sits above the card on a guest
       * sheet, so it is the first thing to fix, and `precheck` has already
       * focused it — stealing the caret back down to the card would scroll the
       * buyer past the field they have to fill in first. Both are marked either
       * way; only the caret is at stake. `emailOk` is a constant true for a
       * signed-in buyer, so their focus is exactly what it was.
       */
      if (emailOk) {
        revealField(
          (subNumberIssue !== null || subNumber.length === 0 ? numberRef : expireRef).current,
          bodyRef.current,
        );
      }
      const reason =
        subNumber.length === 0
          ? 'empty_number'
          : subNumberIssue === 'unsupported'
            ? 'unsupported_bin'
            : subNumberIssue === 'incomplete'
              ? 'incomplete_number'
              : subNumberIssue === 'invalid'
                ? 'invalid_number'
                : subExpire.length === 0
                  ? 'empty_expiry'
                  : subExpireIssue === 'expired'
                    ? 'expired'
                    : subExpireIssue === 'invalid'
                      ? 'invalid_expiry'
                      : 'invalid';
      track('checkout_failed', {
        checkoutId: metaEventId,
        plan: planKey,
        step: 'validate' satisfies CheckoutStep,
        reason,
        attempt: attempts.current.card,
        // The BIN (issuer prefix, never the full PAN) so the allowlist gap is
        // measurable — which unsupported prefixes real buyers actually present.
        ...(reason === 'unsupported_bin' ? { bin: subNumber.slice(0, 6) } : {}),
        // Was the DOM non-empty while what we're submitting is empty — the
        // autofill-wrote-the-DOM-but-not-React-state case.
        ...(reason === 'empty_number' ? { domHadValue } : {}),
      });
      return;
    }

    // The address was malformed. The card is fine, so there is nothing else to
    // say — `precheck` has already shown the sentence and put the caret in the
    // field. Unreachable for a signed-in buyer (`emailOk` is a constant true).
    if (!emailOk) return;

    setError(null);
    setOctoOffer(false);
    setCorporateOffer(false);
    setLoading(true);
    cardSubmitInFlight.current = true;

    /*
     * GUEST: create the account (or sign the buyer in) HERE, between a valid
     * card and Payme — never on a sheet of its own.
     *
     * Order is load-bearing in both directions. AFTER validation, so a typo in
     * the card number cannot mint an account for someone who never pays; BEFORE
     * tokenization, so `subscribe-card` at the end of the OTP has a `user_id` to
     * write (`payment_orders.user_id` is NOT NULL and stays so).
     *
     * A `false` here is not necessarily a failure: `code_required` and
     * `password_required` both return false and reveal their one field under the
     * email. The card stays typed — it is this component's own state and nothing
     * unmounts — and the buyer presses the SAME button again.
     *
     * Resolves true with no request for a signed-in buyer.
     */
    if (identify && !(await identify.ensure())) {
      setLoading(false);
      cardSubmitInFlight.current = false;
      return;
    }

    // The first boundary where the browser hands off to Payme and the server
    // stops being able to see. The MARKETING copy of this rung
    // (Meta AddPaymentInfo + GA4 `add_payment_info`) now fires one step earlier,
    // on the first digit, from /plans — for the one-time and Octobank flows too,
    // which this handler never covered.
    track('payment_info_submitted', {
      checkoutId: metaEventId,
      plan: planKey,
      attempt: attempts.current.card,
    });
    try {
      const created = await cardsCreate(formatCardNumber(subNumber), subExpire);
      const sent = await requestSmsCode(created.token);
      /*
       * `cooldown` here means a code for THIS token went out moments ago and
       * our gate refused a second one. Advance to the OTP step anyway — the SMS
       * the buyer is waiting for is already on its way — but claim nothing we
       * did not do: no `otp_requested`, and no masked phone we were never told.
       */
      if (!sent.cooldown) {
        setPhone(sent.phone ?? null);
        /*
         * The SMS was REQUESTED successfully. Splits the OTP step in two: a user
         * stuck here with `otp_requested` and no `otp_submitted` never received or
         * never entered the code, while a missing `otp_requested` means the request
         * itself failed and no code was ever going to arrive. Those look
         * identical from the outside and have nothing in common as fixes.
         */
        track('otp_requested', { checkoutId: metaEventId, plan: planKey });
      }
      setPending(created);
      // A fresh token means a fresh SMS: drop any code from the previous submit
      // so the buyer can't send the old (now-invalid) code against the new token.
      setCode('');
      // …and a code that died on the previous token says nothing about this one.
      setOtpDead(false);
      setStep('otp');
      setResendIn(resendSeconds(sent.waitMs, RESEND_FIRST_S));
      stepEnteredAt.current = Date.now();
    } catch (err) {
      // The card passed client validation (a known brand, Luhn-valid), so
      // `subNumber` always resolves to a brand here — carried alongside the
      // BIN so we can finally see which brands PAYME itself refuses, not just
      // which prefixes we used to refuse ourselves.
      const brand = detectBrand(subNumber);
      const reason = failureReason(err);
      // Payme's own wording for a `payme_<code>` refusal — the CODE is a family
      // (−31300 alone covers four refusals), and the Cards API code list is not
      // documented anywhere here, so until 2026-10-06 a code like −31502 (11
      // refusals on 05.10, four buyers) could only be read as "something
      // Payme said". Same key as the charge-step event (`detail`), which the
      // API already fills with the receipt refusal text.
      const detail = paymeMessage(err);
      // One `checkout_failed` with a `reason`, not an event per failure mode.
      // The UI collapses everything here into one message, so without the
      // reason a declined card and an unreachable Payme look identical.
      track('checkout_failed', {
        checkoutId: metaEventId,
        plan: planKey,
        step: 'tokenize' satisfies CheckoutStep,
        reason,
        ...(detail ? { detail } : {}),
        attempt: attempts.current.card,
        // Same shape as the validate-step 'unsupported_bin' event — BIN only,
        // never the full PAN.
        bin: subNumber.slice(0, 6),
        ...(brand ? { brand } : {}),
      });
      /*
       * ⚠️ Show what Payme SAID, not a generic sentence — UNLESS Payme itself
       * REFUSED a Visa/Mastercard card (a `payme_<code>` reason, never a
       * network/API failure, which is a retry, not a verdict). That refusal has
       * one fix and it isn't retrying: the Octobank switch when it is on offer
       * (Octobank clears Visa/MC — the recovery this offer was built for), else
       * the one-time «Оплатить разово» button on the plan. Keying this on brand
       * alone would tell a Visa holder with a transient blip that their card is
       * unsupported and steer them out of auto-renewal.
       *
       * Every OTHER tokenization failure — a blocked card, a wrong number, an
       * expired one, no funds — collapsed into «Ошибка карты», which tells the
       * buyer nothing about what to do next. `PaymeCardError` already carries
       * Payme's own message, and the client already prefers its `.ru` variant,
       * so that text was being fetched and then thrown away.
       *
       * Deliberately NOT a code→copy mapping for the rest. The spec asked for
       * «Недостаточно средств — пополните карту», but the Cards/Subscribe error
       * codes are not documented anywhere in this repo (only the Merchant-API
       * JSON-RPC ones are), and guessing which numeric code means "no funds"
       * risks telling someone to top up a card that was actually blocked —
       * worse than saying nothing specific. Payme's own wording is accurate by
       * construction. When their code list is confirmed, map the known ones
       * ABOVE this line and keep this as the fallback.
       */
      const foreignBrandRefused =
        (brand === 'visa' || brand === 'mastercard') && reason.startsWith('payme_');
      /*
       * −31300 is Payme's bucket for four refusals (wrong number, no such
       * card, wrong expiry, corporate card) — the CODE cannot tell them apart,
       * only the wording can, and Payme sends it in the page's language:
       * «корпоратив» / «korporativ» / «corporate». A corporate card is the one
       * of the four with a fix that is not re-typing: Octobank, which takes it.
       */
      const corporateRefused =
        reason === 'payme_-31300' && CORPORATE_CARD_RE.test(paymeMessage(err) ?? '');
      if (corporateRefused && onSwitchToOcto) {
        setCorporateOffer(true);
        revealField(numberRef.current, bodyRef.current);
      } else if (foreignBrandRefused && onSwitchToOcto) {
        /*
         * ⚠️ ONE notice, under the FIELD — no footer box (Igor, 2026-09-20:
         * «и под кнопкой, и под полем карты показывает ошибку и предлагает
         * перейти в Octobank»).
         *
         * The number field already carries a red `noticeBox` with the Octobank
         * switch from the moment a Visa/Mastercard prefix is typed. Setting
         * `error` here printed the same refusal AND the same link a second time
         * in the footer — two red boxes and two «Оплатить через Octobank»
         * buttons on one 375px screen, disagreeing in weight.
         *
         * So the refusal PROMOTES the existing notice instead of adding a
         * second one: `octoOffer` swaps its leading sentence from the warning
         * («Payme принимает только Uzcard и Humo») to the verdict («эта карта
         * не поддерживается через Payme»), and `revealField` scrolls that
         * notice onto the screen with the field it belongs to. The press still
         * produces new information; it produces it in one place.
         *
         * `error` is deliberately left null — the footer box is for failures
         * the field cannot explain, and this is not one of them.
         */
        setOctoOffer(true);
        revealField(numberRef.current, bodyRef.current);
      } else if (foreignBrandRefused) {
        // No Octobank on offer means no inline notice either — it renders only
        // when there is a switch to offer — so the footer is the only place
        // left to say this.
        setError(t('billing.card.errorPaymeBrandUnsupported'));
      } else if (reason === 'payme_-31001') {
        /*
         * −31001 at THIS step arrives with Payme's «Сервер "Paycom" недоступен.
         * Попробуйте позже» — and it is what a card number Payme does not know
         * gets (measured 2026-10-04, desktop and Instagram Android, while real
         * cards were tokenizing fine minutes earlier). Shown as is, a buyer who
         * mistyped a digit reads «the service is down» and leaves. Our line
         * names the fix first and still covers a real outage («попробуйте
         * позже»). Tokenize step only: at charge the same code means no funds.
         */
        setError(t('billing.card.errorCardNotAccepted'));
      } else if (reason === 'payme_-32999') {
        /*
         * −32999 is Payme's own outage («Сервис EPOS временно недоступен») —
         * nothing about this card. The owner's line (2026-10-16) says what to
         * do: later, or Octobank. Plain text, no switch. A Visa/Mastercard with
         * the same code never gets here: the `foreignBrandRefused` branches
         * above answer it first, exactly as before.
         */
        setError(t('billing.card.errorPaymeUnavailable'));
      } else {
        setError(paymeMessage(err) ?? t('billing.card.errorCard'));
      }
    } finally {
      setLoading(false);
      cardSubmitInFlight.current = false;
    }
  }

  /**
   * «Отправить ещё раз».
   *
   * The cooldown starts on a SUCCESSFUL request and on a refusal alike — a 429
   * means a code is already in flight, so the honest answer is the same
   * countdown, never «неверный код», which is what the buyer read on 2026-09-23
   * after typing the code from the first of two SMS. Only a genuine failure
   * (Payme unreachable) leaves the control available, with the error under it.
   */
  async function handleResend() {
    if (!pending || loading || resendInFlight.current) return;
    // Read on the press: the render this handler belongs to is the one the
    // buyer saw the dead-code state in.
    const wasDead = otpDead;
    resendInFlight.current = true;
    setResending(true);
    setError(null);
    try {
      /*
       * After a DEAD code the new one comes on a NEW card token, registered
       * from the card already on this sheet — nothing is retyped. Payme allows
       * three tries per code and its limit is not ours to raise; a fresh token
       * is what reliably brings three fresh ones (it is exactly what «‹ Назад» +
       * «Оплатить» did by hand). 90 days to 2026-10-17: six checkouts hit
       * −31102, none asked for a new code on the old token, five kept typing
       * the dead one into «код истёк» and left. An ORDINARY resend stays on the
       * same token: a second token while the first code is still live is the
       * 2026-09-23 «неверный код».
       */
      let token = pending.token;
      if (wasDead) {
        const created = await cardsCreate(formatCardNumber(numberDigits), expireDigits);
        setPending(created);
        token = created.token;
      }
      const sent = await requestSmsCode(token);
      setResendIn(resendSeconds(sent.waitMs, RESEND_NEXT_S));
      /*
       * A new code is on its way — the confirm button may work again, and the
       * caret goes back into the (cleared) boxes for it. A 429 (`cooldown`)
       * counts too: it means a code for this token went out seconds ago, which
       * is newer than the dead one. Only after a dead code: an ordinary resend
       * leaves focus exactly where it was.
       */
      if (wasDead) {
        setOtpDead(false);
        document
          .getElementById(otpFormId)
          ?.querySelector<HTMLInputElement>('input')
          ?.focus({ preventScroll: true });
      }
      if (sent.cooldown) return;
      setPhone(sent.phone ?? null);
      track('otp_requested', { checkoutId: metaEventId, plan: planKey });
    } catch {
      setError(t('billing.card.errorResend'));
    } finally {
      resendInFlight.current = false;
      setResending(false);
    }
  }

  async function handleOtpSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pending || !otpValid || loading || otpDead) return;
    setError(null);
    setLoading(true);
    attempts.current.otp += 1;
    track('otp_submitted', {
      checkoutId: metaEventId,
      plan: planKey,
      attempt: attempts.current.otp,
    });
    try {
      const verified = await cardsVerify(pending.token, codeDigits);
      if (mode === 'subscribe' && planKey) {
        // The browser begin-checkout (Meta Pixel + GA4) already fired on the
        // «Подписаться» click; thread its metaEventId so the server CAPI
        // InitiateCheckout (fired in POST /billing/subscribe-card, before the
        // charge) dedups against it.
        await subscribeWithCard({
          token: verified.token,
          maskedPan: verified.maskedPan,
          planKey,
          metaEventId,
        });
        /*
         * The BROWSER leg of the purchase conversion (Meta Pixel `Subscribe`),
         * sent from HERE, before `onSaved()` hands over to /billing/return: that
         * page sends it too, but it is a navigation away — a tab closed during
         * the post-charge spinner, or a slow session refresh, and the browser
         * never reports the purchase Meta already holds server-side. Same
         * server-minted event id on both sends, so the pair collapses into one.
         * Bounded to 2 s and never throws; the spinner covers the wait.
         *
         * ⚠️ NO identity rides on this call, for a guest or anyone else. It
         * briefly did: on 2026-09-26 the guest's typed e-mail was handed down
         * so `trackPurchaseConfirmed` could apply it with
         * `fbq('set','userData', …)` before firing. Measured against production
         * the same day — fbevents.js REJECTS that call ("invalid pixel_id") and
         * the pixel's stored `userData` never moved — so the argument was
         * removed along with the attach. The e-mail still reaches Meta, on the
         * CAPI leg of the server's own Subscribe/Purchase.
         */
        await sendPurchaseConversionNow();
      } else {
        await saveCard({ token: verified.token, maskedPan: verified.maskedPan });
        // Attaching a card (or resuming after a failed charge) opts into
        // auto-renew; a plain card CHANGE keeps the current state so a cancelled
        // user isn't silently re-subscribed.
        if (!keepAutoRenew) await setAutoRenew(true);
      }
      /*
       * GUEST: the money has moved — NOW the browser becomes a member.
       *
       * This is the whole «гость до подтверждения оплаты» rule in one line, and
       * its position is the rule. Before the charge the buyer is a guest whose
       * email is still editable, so a refused card leaves nothing behind but a
       * sheet he can correct; after it, the session he paid for. `subscribeWithCard`
       * above carried the checkout token itself (it is a `/billing/*` path), so
       * the charge never needed a cookie to work.
       *
       * Never throws — see `GuestIdentify.commit`. A paid buyer must not be
       * shown the failure text below because the LAST step went wrong.
       *
       * A no-op for a signed-in buyer and for a guest who proved a code or a
       * password, both of whom already have a real session.
       */
      await identify?.commit();
      completed.current = true;
      onSaved();
    } catch (err) {
      // ⚠️ NOT every failure here is a bad SMS code.
      //
      // This step verifies the code AND charges the first period, so the same
      // catch caught the server's plan-level refusals — `tin_required` most of
      // all — and reported them as «неверный код из SMS». A buyer whose code
      // was correct was told it was wrong, at the end of the longest flow on
      // the site, with their card already tokenized. They would retry the code,
      // fail identically, and conclude payment is broken.
      //
      // Only an actual verification failure may claim the code is wrong.
      /*
       * The same distinction the message makes, recorded.
       *
       * `tin_required` is a PLAN-level refusal that used to be reported as
       * «неверный код из SMS» — a buyer with a correct code was told it was
       * wrong, at the end of the longest flow on the site. That class of bug
       * was invisible; now it is a one-line breakdown of `reason`.
       */
      const reason = failureReason(err);
      // The call verifies the code AND charges, so name which half refused.
      // `charge_failed` is the server's verdict on the CHARGE: the code was
      // right, the card is verified and on file, the bank said no.
      const chargeRefused = reason === 'tin_required' || reason === 'charge_failed';
      // Payme's own sentence for a `payme_<code>` verify refusal — the same
      // `detail` the tokenize step has carried since 06.10 and the charge step
      // fills server-side. Until 23.10 this step wrote none: 87 otp refusals in
      // the 30 days before (−31103 ×45, −31110 ×12, −31101 ×12, −31102 ×7,
      // −31100 ×6, −31104 ×5) and not one row said whether the code was wrong,
      // expired or the attempts were used up. `gatewayMessage` has already
      // picked the page's language out of Payme's `{ru, uz, en}`; a server
      // refusal (`ApiError`, string code) yields null here on purpose — the
      // API already writes `providerText` for the charge half.
      const detail = paymeMessage(err);
      track('checkout_failed', {
        checkoutId: metaEventId,
        plan: planKey,
        step: (chargeRefused ? 'charge' : 'otp') satisfies CheckoutStep,
        reason,
        ...(detail ? { detail } : {}),
        attempt: attempts.current.otp,
      });
      if (err instanceof ApiError && err.code === 'tin_required') {
        setError(t('billing.card.errorTinRequired'));
      } else if (err instanceof ApiError && err.code === 'charge_failed') {
        /*
         * A refused charge is not a wrong SMS code. Until now this branch
         * said «Неверный код»: a buyer with a correct code, at the end of
         * the longest flow on the site, was told the code was wrong, retried
         * it (which fails differently — the token is already verified) and
         * concluded payment is broken. Measured on prod 2026-09-14: one
         * buyer with an empty card went on to try two other rails and four
         * plans. Show the gateway's own reason when it sent one, and go back
         * to the card step, because the useful next move is a DIFFERENT card.
         */
        // In the page's language: the API forwards Payme's `{ru, uz, en}` as
        // `messages`, and an explicit `messages: null` means the gateway said
        // nothing readable — then our own line, never the server's Russian
        // fallback (tester, 2026-09-23: «ошибки по карте на русском остались»).
        const details = err.details as
          | { messages?: Record<string, string> | null; reason?: string; cause?: string | null }
          | undefined;
        /*
         * ⚠️ Payme sends `receipts.pay` refusals as a PLAIN RUSSIAN STRING, not
         * `{ru, uz, en}` — every one of the 17 refusals on prod 14–28.09 arrived
         * that way (−31630 «Недостаточно денег на карте» ×16, −31400 «Карта не
         * найдена» ×1). So `messages` was null every time, and since 23.09 the
         * branch below fell through to the generic «банк отклонил списание,
         * попробуйте другую карту» on BOTH locales — a buyer whose card had no
         * money on it was told to change cards (Igor, 2026-09-28: «у клиента
         * 699к вышло „банк не принял“, а не „недостаточно средств“»). The API
         * now reads the MEANING out of that sentence (`cause`), and a cause we
         * have a line for gets it in the page's language; the gateway's
         * per-language text, when it ever sends one, is next; the generic line
         * is last.
         */
        const known = details?.cause ? CHARGE_CAUSE_COPY[details.cause] : undefined;
        const gateway = known
          ? t(known)
          : details?.messages
            ? gatewayMessage(details.messages, locale).trim()
            : details && 'messages' in details
              ? ''
              : err.message.trim();
        setError(gateway.length > 0 ? gateway : t('billing.card.errorChargeFailed'));
        setPending(null);
        setCode('');
        setStep('card');
      } else {
        /*
         * The OTP step itself refused. «Неверный код» is right for -31103 and
         * for anything without a Payme code (network, our own 4xx). It is WRONG
         * for an expired code (-31101) and for exhausted attempts (-31102) —
         * the buyer's next move there is «Отправить ещё раз», not retyping —
         * and for a card-level refusal Payme surfaces on `cards.verify`
         * (-31301 «карта заблокирована/устарела», -31400 «карта не найдена»),
         * where Payme's own sentence, already in the page's language, says
         * what the code cannot.
         */
        const paymeCode = (err as { code?: unknown } | null | undefined)?.code;
        const ownLine = typeof paymeCode === 'number' ? OTP_CODE_COPY[paymeCode] : undefined;
        if (ownLine) {
          setError(t(ownLine));
          // Terminal: this code cannot pass any more, so it is cleared and the
          // confirm button waits for a new one (see `otpDead`).
          if (paymeCode === -31101 || paymeCode === -31102) {
            setCode('');
            setOtpDead(true);
          }
        } else if (typeof paymeCode === 'number' && paymeCode !== -31103 && paymeMessage(err)) {
          setError(paymeMessage(err) ?? t('billing.card.errorOtp'));
        } else {
          setError(t('billing.card.errorOtp'));
          /*
           * A wrong code (−31103) is cleared, with the caret back in the first
           * box. Left on screen, it was one press from being sent again — and
           * Payme allows three tries: the owner, 2026-10-17, burned the last two
           * ten seconds apart and met «попытки исчерпаны». Typing over it was
           * worse still (a digit typed into a full box used to join the old
           * one). The first box is also where iOS offers the code from Messages.
           * A network failure keeps the code: it was never judged.
           */
          if (paymeCode === -31103) {
            setCode('');
            refocusCode.current = true;
          }
        }
      }
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  // Period qualifier on the tariff NAME (e.g. «Pro на месяц»), not the price.
  const periodLabel =
    billingPeriod === 'monthly'
      ? t('billing.card.periodMonthly')
      : billingPeriod === 'annual'
        ? t('billing.card.periodAnnual')
        : '';

  // 16px, not 14px: iOS Safari auto-zooms into any focused input whose font is
  // under 16px, and the page never zooms back out — so switching gateway and
  // focusing the card field left the modal zoomed and clipped on the right.
  const inputBase =
    'h-11 w-full rounded-lg border bg-bone-surface px-3 text-[16px] text-ink placeholder:text-ink-muted focus:outline-none';
  const inputOk = 'border-edge focus:border-brand';
  const inputErr = 'border-status-closing-text focus:border-status-closing-text';
  const labelClass = 'mb-1.5 block text-[12.5px] font-semibold text-ink';
  const fieldError = 'mt-1 text-[12px] font-medium leading-snug text-status-closing-text';
  // Form-level error as a bordered/tinted box UNDER the button (the app's
  // established alert style — see subscription-management / buyer-trust-strip).
  const errorBox =
    'mt-3 rounded-md border border-status-closing-text/30 bg-status-closing-bg px-3 py-2.5 text-center text-[12.5px] font-medium leading-snug text-status-closing-text';
  // The DANGER tone (Igor, 2026-09-20: red), sized for an inline notice that
  // sits under a field rather than under the button: left-aligned, tighter.
  //
  // ⚠️ `danger`, not `status-closing-*`. Those are the amber DEADLINE chip —
  // a warning that a tender closes soon — and this is a refusal: Payme will not
  // take this card. Amber under a red inline error read as the same class of
  // thing as «Платёж защищён — обрабатывает Payme» two lines below and was
  // scrolled past. `errorBox` above still carries the older token; changing it
  // moves every form-level error on this sheet and is a separate call.
  const noticeBox =
    'mt-1.5 rounded-md border border-danger/40 bg-danger/5 px-2.5 py-2 text-[12px] font-medium leading-snug text-danger';

  return (
    <RemoveScroll>
      <div
        className="fixed inset-0 z-50 flex items-end justify-center bg-ink/70 md:items-center md:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        onPointerDown={(e) => {
          overlayPress.current = e.target === e.currentTarget;
        }}
        /*
         * ⚠️ THE OTP STEP CANNOT BE CLOSED BY TAPPING BESIDE THE SHEET.
         *
         * 2026-09-22: a buyer who had already entered a card, received the SMS
         * and was typing the code lost the sheet to a backdrop tap — and with
         * it the token, the code and the order, because the next open resets to
         * the card step by design (a stale OTP is worse). On a phone the
         * backdrop is a few millimetres of dark pixels beside a numeric
         * keyboard; that tap is a slip, not a decision.
         *
         * The ✕ still closes from here, and still writes
         * `checkout_dismissed{step:'otp', via:'close'}` — so the abandonment
         * series keeps its meaning and only the accidental half is removed.
         * Escape is deliberate too and is likewise untouched.
         */
        onClick={(e) => {
          if (step === 'otp') return;
          if (e.target === e.currentTarget && overlayPress.current) dismiss('backdrop');
        }}
      >
        <div
          ref={setPanelRef}
          tabIndex={-1}
          /* Height follows the VISUAL viewport (`--sheet-max`), not `dvh`. On iOS
             the software keyboard shrinks the visual viewport but leaves `dvh`
             alone, so a `92dvh` sheet grew taller than the visible area and its
             top — price, plan, renewal dates — sat above the screen edge with
             `scrollTop` still 0. Nothing to scroll to, and the drag-to-dismiss
             arms exactly at `scrollTop <= 0`, so every attempt to pull the top
             into view closed the sheet instead. `--sheet-max` is set from
             `visualViewport.height` below.
             Since 2026-09-20 the panel is a flex COLUMN that does NOT scroll:
             the body scrolls inside it and the pay button is the last row, so
             the CTA is visible without scrolling on a 375×667 phone and still
             sits inside the `--sheet-max`/`--sheet-lift` box — i.e. above the
             keyboard, which a `position:fixed` bar could not manage.
             ⚠️ The no-var fallback is `92vh` with a `supports-[height:1dvh]`
             twin (2026-09-26), never a lone `dvh`: `dvh` shipped in Chrome 108,
             and on Chrome 101 a `dvh` inside `max-height` computes to `none` —
             no cap at all — while `--sheet-lift` still applies as written. See
             the invoice sheet in `routes/plans.tsx` for the measurement. */
          className={`relative mb-[var(--sheet-lift,0px)] flex max-h-[var(--sheet-max,92vh)] supports-[height:1dvh]:max-h-[var(--sheet-max,92dvh)] w-full flex-col overflow-hidden md:mb-0 rounded-t-2xl border border-edge bg-bone-surface shadow-xl outline-none md:max-w-md md:rounded-xl${
            enterAnimation
              ? ' animate-in fade-in-0 slide-in-from-bottom duration-200 md:slide-in-from-bottom-0 md:zoom-in-95'
              : ''
          }`}
          style={swipe.style}
          onClick={(e) => e.stopPropagation()}
          /*
           * ⚠️ Swipe-to-dismiss is OFF on the OTP step — same reason as the
           * backdrop above. The gesture was already measured to be mostly
           * accidental (2026-09-17: 86 of 354 mobile card checkouts dismissed
           * untouched within 15 s, which is why the grab zone shrank to 72 px);
           * on the confirm step the cost of one accidental swipe is a card that
           * has to be re-entered and an SMS that has to be re-sent. Dropping the
           * handlers is what disables it — the hook has no "off" switch and its
           * `dismiss()` is still what the ✕ uses.
           */
          {...(step === 'otp' ? {} : swipe.handlers)}
        >
          {/* SCROLLING BODY — everything except the CTA. The panel above is a
            flex COLUMN that no longer scrolls itself, so THIS is the element
            whose `scrollTop` the swipe hook must read (hence `bodyRef` as its
            3rd argument): the panel's is now permanently 0, which would arm
            drag-to-dismiss on every downward pan — including one meant to
            scroll back up.
            `flex-auto` + `min-h-0`, deliberately not `flex-1`: the overlay is
            `items-end`/`md:items-center`, so the panel's height is
            content-driven, and `flex-1`'s `flex-basis:0` would collapse it to
            the footer alone. `min-h-0` is what lets this shrink past its own
            content once `--sheet-max` clamps the panel.
            `relative` keeps the ✕ and the drag pill scrolling with the content
            exactly as they did when the panel itself was the scroller. */}
          <div
            ref={bodyRef}
            className="relative min-h-0 flex-auto overflow-y-auto overscroll-contain px-5 pb-4 pt-5 md:px-6 md:pt-6"
          >
            {/* Bottom-sheet drag handle (mobile only). The swipe hook ignores drags
            begun on a form control, so this pill is the reliable non-interactive
            grab target — and the visual cue that the sheet dismisses downward. */}
            <div className="-mt-1 mb-3 flex justify-center md:hidden">
              <span aria-hidden="true" className="h-1 w-10 rounded-full bg-charcoal/30" />
            </div>
            {/* ⚠️ h-9 and full-strength ink, matching the onboarding modal
            (Igor, 2026-09-21: «крестики должны быть заметны»). At h-7 in
            `text-ink-muted` it was a grey glyph on a bone header that buyers
            were closing by swiping instead — and a swipe from the price line
            was already closing this sheet by accident. The `before:-inset-2`
            pseudo-element keeps the TAP area where it was: it already gave the
            7×7 box a 44px target, so the visible square grows and the hit box
            does not move.
            `top-0` on phones, `md:top-3` on desktop: the drag pill above sits
            at y=18 (pt-5 − mt-1 + half its 4px), and a 36px ✕ starting at
            y=0 is centred on that same line. At `top-3` its centre was 12px
            lower than the pill (Igor, 2026-09-22: «крестик и шторку выровни,
            кривовато»). Desktop has no pill (`md:hidden`), so there it keeps
            the old inset. */}
            <button
              type="button"
              onClick={() => swipe.dismiss('close')}
              aria-label={t('support.close')}
              className="absolute right-3 top-0 flex h-9 w-9 items-center justify-center rounded-md text-[15px] text-ink md:top-3 transition-colors before:absolute before:-inset-2 before:content-[''] hover:bg-bone hover:text-ink"
            >
              ✕
            </button>

            {/* HEADER — card step: the plan name (aria-labelledby) + big price. OTP
            step: a «‹ Назад» return-to-card link + «Подтвердите оплату» title, so
            the confirm screen reads as its own distinct step. */}
            {mode === 'subscribe' && step === 'otp' && (
              <button
                type="button"
                onClick={() => {
                  setStep('card');
                  setError(null);
                }}
                className="mb-2 -ml-1 inline-flex items-center gap-0.5 text-[13px] font-medium text-ink-soft transition-colors hover:text-ink"
              >
                ‹ {t('billing.card.otpBack')}
              </button>
            )}
            <h2
              id={TITLE_ID}
              className={
                mode === 'subscribe'
                  ? step === 'otp'
                    ? 'mb-5 text-center text-[21px] font-bold tracking-[-0.3px] text-ink'
                    : 'text-[16px] font-semibold text-ink'
                  : 'mb-4 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink'
              }
            >
              {mode === 'subscribe'
                ? step === 'otp'
                  ? t('billing.card.otpTitle')
                  : planName
                    ? `${planName} ${periodLabel}`.trim()
                    : t('billing.card.titleSubscribe')
                : t('billing.card.title')}
            </h2>
            {mode === 'subscribe' && step === 'card' && priceLabel && (
              <div className="mt-1 text-[28px] font-extrabold leading-none tracking-[-0.4px] text-ink">
                {priceLabel}
              </div>
            )}

            {/* VALUE PILL — what the buyer's own niche is worth right now, from live
            data. Card step only: on the OTP step the modal is a focused confirm,
            so the sell/chrome is dropped. */}
            {mode === 'subscribe' && step === 'card' && nicheHint && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-dim px-3 py-1.5 text-[12.5px] font-medium leading-snug text-brand">
                <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-brand" />
                <span>{nicheHintLine(nicheHint, t)}</span>
              </div>
            )}

            {/* Gateway chooser — card step only (switching gateway mid-OTP is moot). */}
            {/* `mt-4`: the strip now follows the price line directly (the dates
            block that used to sit between them moved below the card fields);
            this is the gap that block's own top margin used to provide. */}
            {/* THE TERMS LINE — under the price, above the gateway strip, on
                every payment sheet in the same frame (`CheckoutTermsLine`,
                2026-09-23). The dated three-row «Оплата и продление» box it
                replaces sat under the card fields here and above the button on
                the Octobank sheet. What a buyer has to know before typing a
                card is one sentence: the next charge date, the amount, and that
                two clicks stop it. Same `nextChargeDate` arithmetic as the
                scheduler, so the date cannot drift from the charge. */}
            {mode === 'subscribe' && step === 'card' && nextCharge && (
              <CheckoutTermsLine headline={t('billing.card.refund7')}>
                {t('billing.card.renewLine', { date: nextCharge })}{' '}
                {t(
                  reminderChannel === 'telegram'
                    ? 'billing.card.remindTelegram'
                    : 'billing.card.remindEmail',
                )}{' '}
                {t('billing.card.cancelHow')}
              </CheckoutTermsLine>
            )}
            {step === 'card' && methodTabs && <div className="mt-4">{methodTabs}</div>}

            {/* GUEST: the address, directly ON TOP OF the card number — the
            single difference between this sheet and the one a signed-in buyer
            sees. It used to sit above the payment-method strip, which put the
            gateway radios between the two things the buyer types; moved down,
            the sheet reads as one form: почта → номер карты → срок.

            ⚠️ Still OUTSIDE the `<form>` below, and that is load-bearing, not
            incidental: the pay button is re-attached to that form by `form=`,
            so an Enter pressed in the email would fire a card validation
            against an empty card. Being the form's previous SIBLING is what
            keeps Enter there inert. Card step only, like the strip: by the OTP
            step the account exists and the question is answered. Null for a
            signed-in buyer, so the form is the first thing under the strip
            exactly as before. */}
            {step === 'card' && identify?.node && <div className="mb-4">{identify.node}</div>}

            {step === 'card' ? (
              <form onSubmit={handleCardSubmit} id={cardFormId} className="space-y-3" noValidate>
                {/* Number + expiry side by side: number wide (flex-1), expiry narrow. */}
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <label htmlFor="card-number" className={labelClass}>
                      {t('billing.card.number')}
                    </label>
                    {/* No in-field scheme logos: in the narrow 2-column row they
                    overlapped the last digits. The accepted schemes are shown on
                    the Payme method card above, and an unsupported BIN still gets
                    the inline error + Octobank switch below. */}
                    <input
                      ref={numberRef}
                      id="card-number"
                      inputMode="numeric"
                      autoComplete="cc-number"
                      placeholder="8600 0000 0000 0000"
                      value={number}
                      onChange={(e) => {
                        setNumber(formatCardNumber(e.target.value));
                        // The funnel rung below `checkout_field_completed`. Its
                        // own guard, deliberately not shared with the Meta one
                        // below: that one is per MOUNT and only for a guest.
                        startField('number', e.target.value);
                        /*
                         * THE FIRST DIGIT — a guest's InitiateCheckout (see the
                         * prop). Read off the event's own value, not the state
                         * this line is still setting, and gated on a real DIGIT
                         * so that a pasted «—» or a cleared field cannot count
                         * as the buyer starting to pay.
                         */
                        if (
                          !paymentEntryStarted.current &&
                          /\d/.test(e.target.value) &&
                          onPaymentEntryStarted
                        ) {
                          paymentEntryStarted.current = true;
                          onPaymentEntryStarted();
                        }
                        // A verdict belongs to the card it was given about —
                        // an edited digit retires it, and the notice drops back
                        // to the plain «Payme принимает только Uzcard и Humo».
                        if (octoOffer) setOctoOffer(false);
                        if (corporateOffer) setCorporateOffer(false);
                      }}
                      onBlur={() => setTouched((s) => ({ ...s, number: true }))}
                      required
                      aria-invalid={showNumberError}
                      className={`${inputBase} ${showNumberError ? inputErr : inputOk}`}
                    />
                    {/* 'unsupported' now means a prefix matching NO known brand (Amex,
                      JCB, a typo) — Visa/MC pass through to Payme. The Octobank
                      switch used to sit here for Visa/MC; it lives in the error box
                      below, on Payme's actual refusal of such a card. */}
                    {showNumberError && (
                      <p className={fieldError} role="alert">
                        {numberErrorText}
                        {/* A number this form does not recognise is a dead end on
                            Payme, and until 01.10 the error named four brands and
                            offered nothing to DO (owner, from a recording). Where
                            Octobank is offered, say so and give the same one-tap
                            switch the Visa/Mastercard notice below already has. */}
                        {numberIssue === 'unsupported' && onSwitchToOcto && (
                          <>
                            {' '}
                            <button
                              type="button"
                              onClick={onSwitchToOcto}
                              className="font-bold text-brand underline underline-offset-2"
                            >
                              {t('billing.card.payViaOcto')}
                            </button>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="w-[112px] shrink-0">
                    <label htmlFor="card-expire" className={labelClass}>
                      {t('billing.card.expire')}
                    </label>
                    <input
                      id="card-expire"
                      ref={expireRef}
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      placeholder={t('billing.card.expirePlaceholder')}
                      value={expire}
                      onChange={(e) => {
                        setExpire(formatExpiry(e.target.value));
                        startField('expiry', e.target.value);
                      }}
                      onBlur={() => setTouched((s) => ({ ...s, expire: true }))}
                      required
                      aria-invalid={touched.expire && expireErrorText != null}
                      className={`${inputBase} ${touched.expire && expireErrorText ? inputErr : inputOk}`}
                    />
                    {touched.expire && expireErrorText && (
                      <p className={fieldError} role="alert">
                        {expireErrorText}
                      </p>
                    )}
                  </div>
                </div>
                {/* ⚠️ The DANGER tone, not a grey footnote (Igor, 2026-09-20).
                  Payme will refuse this card, so this is a refusal stated early —
                  but in muted ink, two lines under a red inline error and just
                  above «Платёж защищён — обрабатывает Payme», it read as the same
                  class of reassurance as the trust line and was scrolled past.
                  Same red border + tint as every other card error on this sheet;
                  the Octobank switch stays inside it, because it is the only
                  thing to DO about it.

                  ⚠️ This is the ONLY place the Visa/Mastercard refusal is shown.
                  Once Payme has actually refused (`octoOffer`) the leading
                  sentence becomes the verdict rather than the warning, and the
                  footer error box deliberately renders nothing for this case —
                  see `handleCardSubmit`.

                  ⚠️ A sibling of the number+expiry ROW, not a child of the number
                  column: inside `flex-1` it was ~209px wide at 375px and broke
                  this sentence over five lines, which is what a "one notice"
                  rule is supposed to avoid. Still directly under the field it is
                  about, and still above the trust block. */}
                {!showNumberError && foreignBrandTyped && onSwitchToOcto && (
                  <p className={noticeBox} role="alert">
                    {t(
                      octoOffer
                        ? 'billing.card.errorUnsupportedOcto'
                        : 'billing.card.foreignBrandHint',
                    )}{' '}
                    <button
                      type="button"
                      onClick={onSwitchToOcto}
                      className="font-bold text-brand underline underline-offset-2"
                    >
                      {t('billing.card.payViaOcto')}
                    </button>
                  </p>
                )}
                {/* The corporate-card verdict — same box, same switch, under the
                    same field; shown only for a card the brand notice above does
                    not already cover (an Uzcard/Humo), so it is never a second
                    box next to the first. */}
                {!showNumberError && corporateOffer && !foreignBrandTyped && onSwitchToOcto && (
                  <p className={noticeBox} role="alert">
                    {t('billing.card.corporateOcto')}{' '}
                    <button
                      type="button"
                      onClick={onSwitchToOcto}
                      className="font-bold text-brand underline underline-offset-2"
                    >
                      {t('billing.card.payViaOcto')}
                    </button>
                  </p>
                )}
              </form>
            ) : (
              <form onSubmit={handleOtpSubmit} id={otpFormId} className="space-y-4" noValidate>
                {/* Where the SMS went (masked phone or generic) + the charge recap.
                Same font size throughout; only the phone number and the sum are
                bolded. Split on a sentinel (`\u0000` fed to the placeholder) so
                the bold span can wrap exactly the interpolated value, in either
                language's word order. */}
                <div className="space-y-1 text-center">
                  {/* 14 px since 2026-10-17 (owner on an iPhone: «маленький
                      текст на форме ввода SMS»); was 12.5. */}
                  <p className="text-[14px] leading-snug text-ink-soft">
                    {phone
                      ? (() => {
                          const [before, after] = t('billing.card.smsSentTo', {
                            phone: '\u0000',
                          }).split('\u0000');
                          return (
                            <>
                              {before}
                              <span className="font-semibold text-ink">{phone}</span>
                              {after}
                            </>
                          );
                        })()
                      : t('billing.card.smsSentGeneric')}
                  </p>
                  {mode === 'subscribe' && priceLabel && pending && (
                    <p className="text-[14px] font-medium text-ink">
                      {(() => {
                        const [before, after] = t('billing.card.otpChargeLine', {
                          scheme: detectScheme(numberDigits)?.toUpperCase() ?? '',
                          card: pending.maskedPan,
                          amount: '\u0000',
                        }).split('\u0000');
                        return (
                          <>
                            {before}
                            <span className="font-bold">{priceLabel}</span>
                            {after}
                          </>
                        );
                      })()}
                    </p>
                  )}
                </div>

                {/* Six single-digit boxes. */}
                <OtpPins
                  value={code}
                  onChange={(v) => {
                    setCode(v.replace(/\D/g, '').slice(0, 6));
                    // `OtpPins` lifts the JOINED six-box value, so this is the
                    // first digit of the code wherever it was typed or pasted.
                    // A backspace back to empty passes no digit and the guard
                    // has already fired, so neither writes a row.
                    startField('otp', v);
                  }}
                  disabled={loading}
                  label={t('billing.card.otp')}
                />

                {/* ALWAYS on this step, not only after a resend. A buyer who
                    has two messages in his inbox cannot tell which one this
                    sheet is verifying against — and on 2026-09-23 one of them
                    picked the older code and was told it was wrong. One line
                    answers the question before it is asked; it costs nothing to
                    anyone holding a single SMS. */}
                <p
                  data-testid="otp-last-sms-hint"
                  className="text-center text-[13px] leading-snug text-ink-soft"
                >
                  {t('billing.card.otpUseLast')}
                </p>

                {/* Resend, as an MM:SS countdown then an outlined button. Was a
                    10.5 px link until 2026-10-17 — the owner had to hunt for it
                    («нужно прям напрячься, чтобы её увидеть»). After a DEAD code
                    it lives in the footer instead (see the footer button). */}
                {!otpDead && (
                  <div className="text-center">
                    {resendIn > 0 ? (
                      <span
                        data-testid="otp-resend-countdown"
                        className="text-[13px] text-ink-muted"
                      >
                        {t('billing.card.resendIn', { time: formatMmss(resendIn) })}
                      </span>
                    ) : (
                      <button
                        type="button"
                        // `resending` as well as `loading`: the request is on the
                        // wire for a second or two, and until 2026-09-23 nothing
                        // disabled the control while it was.
                        disabled={loading || resending || !pending}
                        onClick={() => void handleResend()}
                        className="inline-flex h-10 items-center justify-center rounded-md border border-brand/40 bg-bone-surface px-4 text-[14px] font-semibold text-brand transition-colors hover:border-brand hover:bg-brand/5 disabled:opacity-60"
                      >
                        {t('billing.card.resend')}
                      </button>
                    )}
                  </div>
                )}

                {/* Anti-fraud: never read the code to anyone. */}
                <p className="text-center text-[12px] leading-snug text-ink-muted">
                  {t('billing.card.otpFraud')}
                </p>
              </form>
            )}

            {/* TRUST BLOCK — under the card fields on the card step (subscribe mode):
            the lock line and the offer/privacy/support links. Since 2026-10-11
            (owner) the lock line says the two things a buyer hesitates over at
            this field — the SMS code comes from Payme, and the card number goes
            straight to Payme, not to us (true: `payme-subscribe-client` calls
            checkout.paycom.uz from the browser, SAQ A-EP) — instead of the
            generic «Платёж защищён». The 7-day refund line moved UP into the
            terms line's headline (`CheckoutTermsLine`), so it is not repeated
            here; the non-refund term stays in the оферта (content/legal.ts §5),
            linked here. */}
            {mode === 'subscribe' && step === 'card' && (
              <div className="mt-4 flex flex-col items-center gap-1.5 text-center text-[12px] leading-snug text-ink-soft">
                <span className="inline-flex items-center justify-center gap-1.5">
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4 shrink-0 text-brand"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <rect x="5" y="11" width="14" height="9" rx="2" />
                    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                  </svg>
                  {t('billing.card.smsFromPayme')}
                </span>
                <span>
                  <Link
                    to="/offer"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-brand hover:underline"
                  >
                    {t('billing.card.offerLink')}
                  </Link>
                  {' · '}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-brand hover:underline"
                  >
                    {t('billing.card.privacyLink')}
                  </Link>
                  {HAS_TELEGRAM && (
                    <>
                      {' · '}
                      <a
                        href={COMPANY.telegramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-brand hover:underline"
                      >
                        {t('billing.card.supportTg')}
                      </a>
                    </>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* STICKY CTA FOOTER — the panel's last flex row, never `position:
            fixed`. Two things follow from being INSIDE the panel. It cannot end
            up under the iOS keyboard: the panel is already capped to the visual
            viewport (`--sheet-max`) and lifted above the keyboard
            (`--sheet-lift`), and the footer rides with it. And nothing is hidden
            underneath it — the body is a SIBLING, not a layer, so the scrollable
            area simply ends where this begins and the trust block, the
            footnotes and the timeline are all reachable by scrolling; no spacer
            or measured footer height is needed.
            The buttons live outside the `<form>` that owns the fields and are
            attached back to it by `form=` id, so they are still real submit
            buttons — Enter in the card number still submits — while the guest
            email field and the gateway strip stay OUT of the form, exactly as
            before (an Enter there must not fire a card validation). */}
          <div className="shrink-0 border-t border-edge bg-bone-surface px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 md:px-6 md:pb-6">
            {step === 'card' ? (
              <>
                {/* ⚠️ NO copy of the field error here (Igor, 2026-09-20). It was
                    added so a buyer looking at «Оплатить» would see why the tap
                    did nothing, but it printed the same sentence twice on one
                    small screen and the two disagreed in weight — a bold red box
                    by the button, a thin red line by the field. The field keeps
                    the message; `revealField` above scrolls that field into the
                    middle of the body scroller and focuses it on the same press,
                    which answers the original problem without a second copy. */}
                {/* No «Оплата подтверждается кодом из SMS» line above the button
                    any more (owner, 2026-10-11): the lock line under the card
                    fields now says where the SMS comes from, and two sentences
                    about one SMS on a phone screen was one too many. */}
                {/*
                 * NOT disabled on `!cardValid` — only on `loading`. A disabled button
                 * fires no DOM events, so an invalid card (unsupported BIN, autofill
                 * that never reached React state) left the buyer poking a dead control
                 * and us with zero telemetry. handleCardSubmit now validates on click
                 * and records a `checkout_failed{step:'validate'}` instead.
                 */}
                <button
                  type="submit"
                  form={cardFormId}
                  disabled={loading}
                  className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[14px] font-bold text-white transition-colors hover:bg-brand-mid disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : mode === 'subscribe' && priceLabel ? (
                    /* «Далее» hid the only fact that matters — that this is a
                     purchase. Naming the amount on the button is not a scare
                     tactic; the buyer already agreed to it on the pricing card,
                     and hiding it here is what makes the next screen a surprise. */
                    t('billing.card.payAmount', { amount: priceLabel })
                  ) : (
                    t('billing.card.next')
                  )}
                </button>
                {/* Error UNDER the button, as a centered bordered/tinted alert box —
                  in the footer WITH the button, so a refusal can never be scrolled
                  out of sight from the control it refers to. */}
                {/* ⚠️ NO Octobank switch here any more (Igor, 2026-09-20). The
                    Visa/Mastercard refusal and its one-tap recovery live in the
                    red notice under the number field, and printing both put two
                    red boxes and two «Оплатить через Octobank» buttons on one
                    phone screen. This box is now only for failures the field
                    cannot explain — a declined Uzcard, a dropped fetch, a
                    gateway fault — which is why those branches still set
                    `error` and the foreign-brand one does not. */}
                {error && (
                  <div className={errorBox} role="alert">
                    <p>{error}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                {otpDead ? (
                  /* After a DEAD code (−31101/−31102) the footer button IS the
                     resend: the code on screen can never pass, so «Подтвердить»
                     has nothing to do, and the one next move — with the wait
                     Payme imposes counted down inside it — sits where the
                     thumb already is. A new code turns it back into confirm. */
                  <button
                    type="button"
                    data-testid="otp-new-code"
                    disabled={loading || resending || !pending || resendIn > 0}
                    onClick={() => void handleResend()}
                    className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[14px] font-bold text-white transition-colors hover:bg-brand-mid disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {resending ? (
                      <Spinner size="sm" />
                    ) : resendIn > 0 ? (
                      t('billing.card.newCodeIn', { time: formatMmss(resendIn) })
                    ) : (
                      t('billing.card.resend')
                    )}
                  </button>
                ) : (
                  <button
                    type="submit"
                    form={otpFormId}
                    disabled={loading || !otpValid}
                    className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[14px] font-bold text-white transition-colors hover:bg-brand-mid disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? (
                      <Spinner size="sm" />
                    ) : mode === 'subscribe' ? (
                      t('billing.card.confirmPay')
                    ) : (
                      t('billing.card.confirm')
                    )}
                  </button>
                )}
                {/* Error UNDER the button, as a centered bordered/tinted alert box.
                  This is where «неверный код» / a plan-level refusal surfaces. */}
                {error && (
                  <p className={errorBox} role="alert">
                    {error}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </RemoveScroll>
  );
}
